import { useState, useRef, useEffect } from "react";
import { drawerHelpSeen, markDrawerHelpSeen } from "../onboarding/drawerHelp";
/** Drawer-help domain: first-open banner state, seen-pending ref, and the 5s-mark effect. */
export function useDrawerHelp({ contractOpen, logOpen, proOpen, accountOpen }) {
  // First-open drawer help (Contract / Forecast Log / Settings). Each banner
  // shows while its drawer is open until dismissed; dismissal persists per
  // drawer. The Sayer drawer is excluded — it hosts the tour itself.
  const [drawerHelpState, setDrawerHelpState] = useState(() => ({
    contract: drawerHelpSeen("contract"),
    log: drawerHelpSeen("log"),
    settings: drawerHelpSeen("settings"),
    account: drawerHelpSeen("account"),
  }));

  // Drawer help (no manual ✕). The banner stays visible the WHOLE time its panel
  // is open — it is never removed while open. After the panel has been open ~5s
  // we mark it "pending"; the banner is only actually retired (hidden + persisted
  // so it won't show again) once the panel is CLOSED. Closing before 5s leaves it
  // to show again next open.
  const drawerSeenPendingRef = useRef({});
  useEffect(() => {
    const panels = [
      [contractOpen, "contract"],
      [logOpen, "log"],
      [proOpen, "settings"],
      [accountOpen, "account"],
    ];
    const timers = [];
    for (const [open, key] of panels) {
      if (open && !drawerHelpState[key] && !drawerSeenPendingRef.current[key]) {
        timers.push(
          setTimeout(() => {
            drawerSeenPendingRef.current[key] = true;
            markDrawerHelpSeen(key);
          }, 5000),
        );
      } else if (!open && drawerSeenPendingRef.current[key]) {
        // Panel closed after the 5s window — now retire the banner.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setDrawerHelpState((s) => (s[key] ? s : { ...s, [key]: true }));
      }
    }
    return () => timers.forEach(clearTimeout);
  }, [contractOpen, logOpen, proOpen, accountOpen, drawerHelpState]);

  return { drawerHelpState, setDrawerHelpState, drawerSeenPendingRef };
}
